import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Copy,
  Ellipsis,
  Pencil,
  Trash2,
  TriangleAlert,
} from "lucide-react";

import { cn, getText } from "shared/lib";
import { type TreeDescriptor, treeName } from "../lib";
import { isTreeItemValid } from "./lib";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "shared/components";
import { TreeItemPropertyList } from "./TreeItemPropertyList";

export interface TreeItemProps {
  hideDelete?: boolean;
  onClick?: () => void;
  onClickDelete?: () => void;
  onClickDuplicate?: () => void;
  selected?: boolean;
  treeDescriptor: TreeDescriptor;
}

export function TreeItem({
  hideDelete,
  onClick,
  onClickDelete,
  onClickDuplicate,
  selected,
  treeDescriptor,
}: TreeItemProps) {
  const [clicked, setClicked] = useState(true);
  const [overflowed, setOverflowed] = useState(false);
  const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);

  const propertiesOuterContainerRef = useRef<HTMLDivElement | null>(null);
  const propertiesInnerContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      propertiesOuterContainerRef.current &&
      propertiesInnerContainerRef.current
    ) {
      setOverflowed(
        propertiesInnerContainerRef.current.scrollWidth >
          propertiesOuterContainerRef.current.offsetWidth,
      );
    }
  }, []);

  const isValid = useMemo(() => {
    if (!clicked) return false;

    return isTreeItemValid(treeDescriptor);
  }, [clicked, treeDescriptor]);

  const isEmpty = useMemo(() => {
    return Array.from(treeDescriptor.properties.values()).every(
      (property) => !("value" in property),
    );
  }, [treeDescriptor.properties]);

  return (
    <div
      className={cn(
        "border-interactiveBorder hover:border-interactiveBorderHover focus-visible:border-interactiveBorderHover flex w-full cursor-pointer justify-between rounded border bg-white px-3 py-2 focus-visible:shadow-[0_0_2px_2px_#0157FF] focus-visible:outline-0",
        selected && "border-interactiveBorderSelect",
      )}
      onClick={(event) => {
        setClicked(true);
        onClick?.();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      tabIndex={0}
    >
      <div className="flex flex-col gap-1 overflow-hidden">
        <div className="flex items-center gap-2">
          <h3 className="text-textPrimary text-headingmd overflow-hidden truncate text-ellipsis">
            {treeName(treeDescriptor)}
          </h3>

          {!isEmpty && (
            <>
              {isValid === true ? (
                <span className="text-greenDefault text-bodysm flex items-center gap-0.5">
                  <Check className="h-4 w-4" /> {getText("Done")}
                </span>
              ) : null}

              {isValid === false ? (
                <span className="text-yellowHover text-bodysm flex items-center gap-0.5">
                  <TriangleAlert className="h-4 w-4" />{" "}
                  <span className="whitespace-nowrap">
                    {getText("Missing info")}
                  </span>
                </span>
              ) : null}
            </>
          )}
        </div>

        <div className="overflow-hidden" ref={propertiesOuterContainerRef}>
          {isEmpty && (
            <Button
              className="text-bodyxs gap-1 underline"
              onClick={(event) => {
                event.stopPropagation();
                setClicked(true);
                onClick?.();
              }}
              size="tight"
              variant="link"
            >
              <Pencil className="max-h-3 max-w-3" />
              {getText("Add details")}
            </Button>
          )}

          {!isEmpty && (
            <div
              className="flex gap-3"
              ref={propertiesInnerContainerRef}
              style={{
                ...(overflowed
                  ? {
                      maskImage:
                        "linear-gradient(to right, black 80%, transparent 100%)",
                    }
                  : {}),
              }}
            >
              <TreeItemPropertyList treeDescriptor={treeDescriptor} />
            </div>
          )}
        </div>
      </div>

      <div>
        <DropdownMenu
          onOpenChange={setDropdownMenuOpen}
          open={dropdownMenuOpen}
        >
          <DropdownMenuTrigger
            className="text-textLink bg-blueBgLight pointer-events-auto cursor-pointer rounded"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Ellipsis className="min-h-5 min-w-5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              className="text-textLink hover:!text-textLink hover:!bg-blueBgLight"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDropdownMenuOpen(false);

                setClicked(true);
                onClick?.();
              }}
            >
              <Pencil className="min-h-4 min-w-4" />{" "}
              {getText("Edit tree details")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-textLink hover:!text-textLink hover:!bg-blueBgLight"
              onClick={(event) => {
                event.stopPropagation();

                onClickDuplicate?.();
              }}
            >
              <Copy className="min-h-4 min-w-4" /> {getText("Duplicate tree")}
            </DropdownMenuItem>

            {!hideDelete && (
              <>
                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="text-redDefault hover:!text-redDefault hover:!bg-redBgLight"
                  onClick={(event) => {
                    event.stopPropagation();

                    onClickDelete?.();
                  }}
                >
                  <Trash2 className="min-h-4 min-w-4" />{" "}
                  {getText("Delete tree")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
